/**
 * Decision Making Assist — the deterministic engine.
 *
 * The arithmetic, separated from the argument. Everything here is computed
 * from platform records and from values a named person entered; a language
 * model, when one is connected, only explains what this module produced and
 * is checked against it (`verifyFigures`). See docs/DECISION-ASSIST.md for
 * the specification this implements, section by section.
 *
 * Pure. The page gathers the records and the entered values; this decides.
 */
import type { ClosureCost } from "./capacity";
import type { Closure } from "./closures";
import type { Finding, IntelligenceReport } from "./intelligence";
import { money } from "./intelligence";
import type { LiabilityRegister } from "./liability";
import type { TypeSummary } from "./testing";
import type { WorkOrder } from "./workOrders";
import { t, tn } from "../i18n";

/* ── §1 sources ───────────────────────────────────────────────────────── */

export type SourceKind =
  | "alert"
  | "rule"
  | "work_order"
  | "test"
  | "test_history"
  | "obligation"
  | "fit"
  | "drift"
  | "liability"
  | "ledger"
  | "hold_point"
  | "closure"
  | "capacity"
  | "rate_card"
  | "entered"
  /* the executive suite's sources */
  | "kpi"
  | "document"
  | "project"
  | "finance"
  | "demo"
  | "playbook"
  /* a screen's shadow runs against the system being replaced */
  | "migration";

export interface SourceRef {
  kind: SourceKind;
  ref: string;
  label: string;
  href?: string;
  enteredBy?: string;
  enteredAt?: string;
}

/* ── §2 one shape ─────────────────────────────────────────────────────── */

export type Category = "pavement" | "agl" | "marking" | "pbb" | "certificate" | "structure" | "systems" | "other";
export type ZoneClass = "runway" | "taxiway" | "stand" | "apron" | "terminal" | "other";
export const CATEGORIES: readonly Category[] = [
  "pavement",
  "agl",
  "marking",
  "pbb",
  "certificate",
  "structure",
  "systems",
  "other",
];
export const ZONE_CLASSES: readonly ZoneClass[] = ["runway", "taxiway", "stand", "apron", "terminal", "other"];

export interface Component {
  key: "consequence" | "timeToThreshold" | "regulatoryExposure" | "deferralCost" | "windowConstraint";
  label: string;
  value: number | null;
  /** the arithmetic, as a reader would check it */
  working: string;
  sources: SourceRef[];
}

export interface Score {
  components: Component[];
  total: number | null;
  /** how many of the five the total rests on */
  coverage: string;
  weights: Weights;
}

export type Weights = Record<Component["key"], number>;
export const DEFAULT_WEIGHTS: Weights = {
  consequence: 0.3,
  timeToThreshold: 0.25,
  regulatoryExposure: 0.2,
  deferralCost: 0.15,
  windowConstraint: 0.1,
};

export interface Option {
  key: "act_now" | "defer_mitigate" | "defer_accept";
  label: string;
  cost: number | null;
  closureMinutes: number | null;
  exposure: number | null;
  mitigation?: string;
  displaces: string[];
  note: string;
}

export interface MissingInput {
  field: "trend" | "closureMinutes" | "movementsPerHour" | "costNow" | "window" | "condition";
  why: string;
}

export interface DecisionItem {
  id: string;
  findingId: string;
  asset: string;
  zone?: string;
  category: Category;
  headline: string;
  condition: {
    value?: number;
    unit?: string;
    limit?: number;
    direction: "higher_is_better" | "lower_is_better";
    label: string;
    sources: SourceRef[];
  };
  trend: { ratePerDay?: number; basis: string; points: number; sources: SourceRef[] };
  clause: {
    framework: string;
    clause: string;
    effect: "finding" | "certificate_action" | "advisory";
    sources: SourceRef[];
  };
  surface: {
    zoneClass: ZoneClass;
    closureMinutes?: number;
    movementsPerHour?: number;
    standType: "contact" | "remote" | "n/a";
    sources: SourceRef[];
  };
  timeToThresholdDays: number | null;
  mandatory: { is: boolean; why?: string };
  cost: { now?: number; quarter?: number; year?: number; twoYears?: number; currency: string; sources: SourceRef[] };
  window: { needsNight: boolean; season?: string; crew?: string; crewNights: number; sources: SourceRef[] };
  score: Score;
  options: [Option, Option, Option];
  missing: MissingInput[];
  evidence: SourceRef[];
}

/* ── §9 the rate card, §11 entered values, the quarter's constraints ──── */

export interface RateCard {
  currency: string;
  categories: Record<
    Category,
    { unitRepairNow: number; escalationPerQuarter: number; mitigationCost: number; mitigation: string }
  >;
  closureCostPerHour: Record<ZoneClass, number>;
  movementsPerHour: Record<ZoneClass, number>;
  enteredBy?: string;
  enteredAt?: string;
}

/** A coarse card to start from. Every figure is a placeholder the airport edits; the screen says so. */
export function defaultRateCard(currency = "SAR"): RateCard {
  const cat = (unitRepairNow: number, escalationPerQuarter: number, mitigationCost: number, mitigation: string) => ({
    unitRepairNow,
    escalationPerQuarter,
    mitigationCost,
    mitigation,
  });
  return {
    currency,
    categories: {
      pavement: cat(45_000, 0.18, 6_000, "restrict the section to daylight and add a weekly friction or PCI check"),
      agl: cat(12_000, 0.1, 2_500, "nightly serviceability round of the circuit and a NOTAM on the affected lights"),
      marking: cat(8_000, 0.08, 1_500, "temporary marking and a weekly retro-reflectivity spot check"),
      pbb: cat(60_000, 0.12, 4_000, "stand restricted to non-bridge boarding with coaching"),
      certificate: cat(5_000, 0.05, 1_000, "written notice to the authority with the renewal date"),
      structure: cat(120_000, 0.15, 8_000, "load restriction and monthly inspection"),
      systems: cat(20_000, 0.1, 3_000, "manual workaround with an hourly check"),
      other: cat(10_000, 0.1, 2_000, "increased inspection frequency"),
    },
    closureCostPerHour: { runway: 45_000, taxiway: 12_000, stand: 6_000, apron: 4_000, terminal: 3_000, other: 1_000 },
    movementsPerHour: { runway: 24, taxiway: 12, stand: 2, apron: 4, terminal: 0, other: 0 },
  };
}

export interface EnteredInputs {
  /** by item id, what a person entered where the platform had nothing */
  [itemId: string]: Partial<{
    ratePerDay: number;
    trendBasis: string;
    closureMinutes: number;
    movementsPerHour: number;
    costNow: number;
    needsNight: boolean;
    crew: string;
    crewNights: number;
    season: string;
    conditionValue: number;
    conditionLimit: number;
    enteredBy: string;
    enteredAt: string;
  }>;
}

export interface Constraints {
  quarter: string;
  windows: number;
  budget: number;
  crews: number;
  nightsPerCrew: number;
  scarceCrew?: string;
  season?: string;
  weights: Weights;
}

export function defaultConstraints(now: Date): Constraints {
  const q = Math.floor(now.getMonth() / 3) + 1;
  return {
    quarter: `${now.getFullYear()} Q${q}`,
    windows: 12,
    budget: 400_000,
    crews: 2,
    nightsPerCrew: 20,
    weights: { ...DEFAULT_WEIGHTS },
  };
}

/* ── inputs to the engine ─────────────────────────────────────────────── */

export interface EngineInputs {
  now: Date;
  report: IntelligenceReport;
  workOrders: WorkOrder[];
  closures: Closure[];
  closureCosts: Record<string, ClosureCost | null>;
  liability: LiabilityRegister | null;
  testTypes: TypeSummary[];
  /** per asset and test type, the readings over time — for the trend */
  histories: Record<
    string,
    { performedOn: string; value: number; specified?: number; reference: string; recordId: string }[]
  >;
  rateCard: RateCard;
  entered: EnteredInputs;
  constraints: Constraints;
  /** resolved predictions on the outcome ledger, for the confidence gate (§10) */
  resolvedPredictions: number;
}

const clamp = (x: number) => Math.max(0, Math.min(1, x));
const DAY = 86_400_000;

function categoryOf(f: Finding): Category {
  const t = `${f.headline} ${f.where ?? ""} ${f.id}`.toLowerCase();
  if (/certif|licen/.test(t)) return "certificate";
  if (/bridge|pbb|boarding/.test(t)) return "pbb";
  if (/lamp|agl|light|circuit|photometric|isolation/.test(t)) return "agl";
  if (/marking|retro|sign\b|signs/.test(t)) return "marking";
  if (/friction|rubber|ravel|pavement|pci|pcr|texture|fod|threshold|runway|taxiway/.test(t)) return "pavement";
  if (/structur|hwd|load/.test(t)) return "structure";
  if (/reclaim|carousel|conveyor|fids|escalator|lift|systems/.test(t)) return "systems";
  return "other";
}

function zoneClassOf(zone: string | undefined, headline: string): ZoneClass {
  const t = `${zone ?? ""} ${headline}`;
  if (/\bRWY|runway|threshold|touchdown/i.test(t)) return "runway";
  if (/\bTWY|taxiway|holding/i.test(t)) return "taxiway";
  if (/stand|gate|pbb|bridge|\b[A-Z]\d{1,2}\b/i.test(t)) return "stand";
  if (/apron|apn/i.test(t)) return "apron";
  if (/terminal|reclaim|carousel|fids/i.test(t)) return "terminal";
  return "other";
}

function sameZone(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  const x = a.toUpperCase();
  const y = b.toUpperCase();
  return x === y || x.endsWith(`-${y}`) || y.endsWith(`-${x}`);
}

const fromEvidence = (e: { kind: string; ref: string; label: string; href?: string }): SourceRef => ({
  kind: (e.kind as SourceKind) ?? "alert",
  ref: e.ref,
  label: e.label,
  href: e.href,
});

function median(xs: number[]): number | undefined {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
}

/** Least-squares slope of value against days, for the trend. */
function slopePerDay(points: { performedOn: string; value: number }[]): number | undefined {
  if (points.length < 2) return undefined;
  const t0 = new Date(points[0].performedOn).getTime();
  const xs = points.map((p) => (new Date(p.performedOn).getTime() - t0) / DAY);
  const ys = points.map((p) => p.value);
  const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
  const my = ys.reduce((a, b) => a + b, 0) / ys.length;
  const sxx = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
  if (sxx === 0) return undefined;
  return xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0) / sxx;
}

/* ── §2 normalise ─────────────────────────────────────────────────────── */

function normalise(f: Finding, inp: EngineInputs): Omit<DecisionItem, "score" | "options"> {
  const missing: MissingInput[] = [];
  const category = categoryOf(f);
  const zone = f.where?.match(/\b(RWY-[\w-]+|TWY-[\w-]+|APRON-\w+|STAND-\w+|[A-Z]\d{1,2})\b/)?.[1];
  const zoneClass = zoneClassOf(zone, f.headline);
  const ent = inp.entered[f.id] ?? {};
  const enteredRef = (what: string): SourceRef => ({
    kind: "entered",
    ref: `entered:${f.id}:${what}`,
    /* `what` stays English in the ref, which is a key; the label is what a reader sees */
    label: ent.enteredBy
      ? t("{what} entered by {name}", { what: t(what), name: ent.enteredBy })
      : t("{what} entered", { what: t(what) }),
    enteredBy: ent.enteredBy,
    enteredAt: ent.enteredAt,
  });
  const cardRef: SourceRef = {
    kind: "rate_card",
    ref: "rate-card",
    label: inp.rateCard.enteredBy
      ? t("rate card (edited by {name})", { name: inp.rateCard.enteredBy })
      : t("rate card (starting card, unedited)"),
    enteredBy: inp.rateCard.enteredBy,
    enteredAt: inp.rateCard.enteredAt,
  };
  const work = f.attribution.evidence
    .filter((e) => e.kind === "work_order")
    .map((e) => inp.workOrders.find((w) => w.number === e.ref))
    .filter((w): w is WorkOrder => !!w);
  /* the asset the work names, else the finding's own place, else the site */
  const asset = work.find((w) => w.assetTag)?.assetTag ?? f.where ?? zone ?? "site";

  /* condition: from the alert's readings against its parameters, from a test's latest value, or entered */
  let condition: DecisionItem["condition"] = {
    direction: "higher_is_better",
    label: t("no measured condition on record"),
    sources: [],
  };
  const readings = f.prediction.source.match(/Readings: ([^·]+)/)?.[1];
  const params = f.prediction.source.match(/Parameters: ([^·]+)/)?.[1];
  const readVal = readings?.match(/= ([0-9.]+)/)?.[1];
  const limVal = params?.match(/(min|max|limit|threshold)[_a-z]* ([0-9.]+)/i)?.[2];
  if (readVal && limVal) {
    const value = Number(readVal);
    const limit = Number(limVal);
    const dir: DecisionItem["condition"]["direction"] = /min/i.test(params ?? "")
      ? "higher_is_better"
      : "lower_is_better";
    condition = {
      value,
      limit,
      direction: dir,
      label:
        dir === "higher_is_better"
          ? t("{value} against minimum {limit}", { value: readVal, limit: limVal })
          : t("{value} against maximum {limit}", { value: readVal, limit: limVal }),
      sources: f.prediction.evidence.map(fromEvidence),
    };
  } else if (ent.conditionValue != null && ent.conditionLimit != null) {
    condition = {
      value: ent.conditionValue,
      limit: ent.conditionLimit,
      direction: "higher_is_better",
      label: t("{value} against {limit} (entered)", { value: ent.conditionValue, limit: ent.conditionLimit }),
      sources: [enteredRef("condition")],
    };
  } else {
    missing.push({
      field: "condition",
      why: t("no reading against a limit on record; enter the measured value and the limit"),
    });
  }

  /* trend: from the asset's test history, from a predictive date, or entered */
  let trend: DecisionItem["trend"] = { basis: t("not computable: no condition history"), points: 0, sources: [] };
  const historyKey = Object.keys(inp.histories).find(
    (k) => asset && k.toUpperCase().endsWith(`:${asset.toUpperCase()}`) && inp.histories[k].length >= 2,
  );
  const history = historyKey ? inp.histories[historyKey] : [];
  if (history.length >= 2) {
    const sorted = [...history].sort((a, b) => a.performedOn.localeCompare(b.performedOn));
    const rate = slopePerDay(sorted);
    const span = Math.round(
      (new Date(sorted[sorted.length - 1].performedOn).getTime() - new Date(sorted[0].performedOn).getTime()) / DAY,
    );
    trend = {
      ratePerDay: rate,
      basis:
        rate != null
          ? t("{n} readings over {days} days, least-squares slope {rate} per day", {
              n: sorted.length,
              days: span,
              rate: rate.toFixed(4),
            })
          : t("not computable: readings on one day"),
      points: sorted.length,
      sources: sorted.slice(-3).map((p) => ({
        kind: "test_history",
        ref: p.reference,
        label: t("{value} on {date}", { value: p.value, date: p.performedOn.slice(0, 10) }),
        href: `/testing-records/${p.recordId}`,
      })),
    };
    if (condition.value == null) {
      const last = sorted[sorted.length - 1];
      condition = {
        value: last.value,
        limit: last.specified,
        direction: "higher_is_better",
        label: `${t("{value} on {date}", { value: last.value, date: last.performedOn.slice(0, 10) })}${last.specified != null ? t(" against {limit}", { limit: last.specified }) : ""}`,
        sources: trend.sources,
      };
      missing.splice(
        missing.findIndex((m) => m.field === "condition"),
        1,
      );
    }
  } else if (ent.ratePerDay != null) {
    trend = {
      ratePerDay: ent.ratePerDay,
      basis: ent.trendBasis ?? t("entered, not measured"),
      points: 0,
      sources: [enteredRef("trend")],
    };
  } else if (f.phase === "ahead" && f.horizon.days != null) {
    trend = {
      basis: t("the rule's own forecast: {forecast}", { forecast: f.horizon.text }),
      points: 0,
      sources: f.prediction.evidence.map(fromEvidence),
    };
  } else {
    missing.push({
      field: "trend",
      why: t("no condition history to fit a trend; enter the rate of change per day and where it comes from"),
    });
  }

  /* time to threshold */
  let ttt: number | null = null;
  if (condition.value != null && condition.limit != null && trend.ratePerDay != null && trend.ratePerDay !== 0) {
    const gap =
      condition.direction === "higher_is_better"
        ? condition.value - condition.limit
        : condition.limit - condition.value;
    const toward = condition.direction === "higher_is_better" ? -trend.ratePerDay : trend.ratePerDay;
    ttt = gap <= 0 ? 0 : toward > 0 ? Math.round(gap / toward) : null;
    if (ttt === null)
      trend = {
        ...trend,
        basis: t("{basis}; the trend runs away from the limit, so no threshold date", { basis: trend.basis }),
      };
  } else if (f.phase === "ahead" && f.horizon.days != null) {
    ttt = f.horizon.days;
  } else if (f.phase === "now") {
    ttt = 0;
    if (trend.ratePerDay == null)
      trend = { ...trend, basis: t("already at the limit: the finding is open on the platform now") };
  }

  /* clause */
  const clauseText = f.defensibility.regulatory[0];
  const effect: DecisionItem["clause"]["effect"] =
    category === "certificate" || /certif/i.test(clauseText ?? "")
      ? "certificate_action"
      : clauseText
        ? "finding"
        : "advisory";
  const clause: DecisionItem["clause"] = {
    framework: clauseText ? clauseText.split(/[;§]/)[0].trim() : t("no clause on the rule"),
    clause: clauseText ?? "",
    effect,
    sources: f.prediction.evidence.filter((e) => e.kind === "rule").map(fromEvidence),
  };

  /* surface: closure minutes from history or labour; movements from the capacity model or the card */
  const history2 = inp.closures.filter((c) => sameZone(c.zoneCode, zone) && c.minutes != null);
  const med = median(history2.map((c) => c.minutes as number));
  const labour = work.find((w) => w.labourHours != null);
  const cc = zone ? inp.closureCosts[zone] : undefined;
  const surfaceSources: SourceRef[] = [];
  let closureMinutes: number | undefined;
  if (ent.closureMinutes != null) {
    closureMinutes = ent.closureMinutes;
    surfaceSources.push(enteredRef("closure minutes"));
  } else if (med != null) {
    closureMinutes = med;
    surfaceSources.push(
      ...history2.slice(0, 3).map((c) => ({
        kind: "closure" as const,
        ref: c.reference ?? c.id.slice(0, 8),
        label: `${c.minutes} min · ${c.reason}`,
      })),
    );
  } else if (labour?.labourHours != null) {
    closureMinutes = Math.round(labour.labourHours * 60);
    surfaceSources.push({
      kind: "work_order",
      ref: labour.number,
      label: t("{n} labour hours planned", { n: labour.labourHours }),
      href: `/work-orders/${labour.id}`,
    });
  } else
    missing.push({
      field: "closureMinutes",
      why: t("no closure of this zone on record and no labour hours planned; enter the closure the work needs"),
    });
  let movementsPerHour: number | undefined;
  if (ent.movementsPerHour != null) {
    movementsPerHour = ent.movementsPerHour;
    surfaceSources.push(enteredRef("movements per hour"));
  } else if (cc && cc.known && cc.hours) {
    movementsPerHour = Math.round((cc.movements / cc.hours) * 10) / 10;
    surfaceSources.push({
      kind: "capacity",
      ref: cc.position,
      label: t("{movements} movements in {hours} h from the capacity model", {
        movements: cc.movements,
        hours: cc.hours,
      }),
    });
  } else if (inp.rateCard.movementsPerHour[zoneClass] > 0) {
    movementsPerHour = inp.rateCard.movementsPerHour[zoneClass];
    surfaceSources.push(cardRef);
  } else
    missing.push({
      field: "movementsPerHour",
      why: t("no capacity model coverage and no movements figure on the card for this kind of zone"),
    });
  const standType: DecisionItem["surface"]["standType"] =
    zoneClass === "stand" ? (/remote/i.test(f.headline) ? "remote" : "contact") : "n/a";

  /* cost: the register, actual work-order cost, entered, or the card */
  const currency = inp.rateCard.currency;
  const row = inp.liability?.register.find(
    (r) => r.priced && r.costNow != null && [asset, zone].some((k) => k && r.title.includes(k)),
  );
  const card = inp.rateCard.categories[category];
  const costSources: SourceRef[] = [];
  let now: number | undefined;
  if (row) {
    now = row.costNow;
    costSources.push({ kind: "liability", ref: row.id, label: row.title, href: "/liability" });
  } else if (work.find((w) => w.costPlanned != null || w.costActual != null)) {
    const w = work.find((x) => x.costPlanned != null || x.costActual != null)!;
    now = w.costActual ?? w.costPlanned;
    costSources.push({
      kind: "work_order",
      ref: w.number,
      label: w.costActual != null ? t("actual cost on the work order") : t("planned cost on the work order"),
      href: `/work-orders/${w.id}`,
    });
  } else if (ent.costNow != null) {
    now = ent.costNow;
    costSources.push(enteredRef("repair cost"));
  } else if (card.unitRepairNow > 0) {
    now = card.unitRepairNow;
    costSources.push(cardRef);
    missing.push({
      field: "costNow",
      why: t("priced from the starting rate card ({amount} for {category}); enter this job's own estimate", {
        amount: money(card.unitRepairNow, currency),
        category,
      }),
    });
  }
  const esc = row?.escalation ?? card.escalationPerQuarter;
  const cost: DecisionItem["cost"] = {
    now,
    quarter: now != null ? Math.round(now * (1 + esc)) : undefined,
    year: row?.costDeferred ?? (now != null ? Math.round(now * (1 + esc) ** 4) : undefined),
    twoYears: now != null ? Math.round(now * (1 + esc) ** 8) : undefined,
    currency,
    sources: costSources.length ? costSources : [],
  };

  /* window */
  const needsNight = ent.needsNight ?? (zoneClass === "runway" || zoneClass === "taxiway");
  const crew = ent.crew ?? work.find((w) => w.crew)?.crew ?? work.find((w) => w.discipline)?.discipline;
  const window: DecisionItem["window"] = {
    needsNight,
    season: ent.season,
    crew,
    crewNights: ent.crewNights ?? (closureMinutes != null ? Math.max(1, Math.ceil(closureMinutes / 360)) : 1),
    sources: [
      ...(ent.needsNight != null || ent.crew || ent.crewNights != null ? [enteredRef("window")] : []),
      ...work
        .filter((w) => w.crew || w.discipline)
        .slice(0, 1)
        .map((w) => ({
          kind: "work_order" as const,
          ref: w.number,
          label: t("{crew} on the work order", { crew: String(w.crew ?? w.discipline) }),
          href: `/work-orders/${w.id}`,
        })),
    ],
  };
  if (!crew)
    missing.push({
      field: "window",
      why: t("no crew or discipline on any work for this; enter who does it and how many nights it takes"),
    });

  /* §4 mandatory */
  let mandatory: DecisionItem["mandatory"] = { is: false };
  const part139 = /Part 139|Annex 14/i.test(clauseText ?? "");
  if (f.id.startsWith("alert:") && f.phase === "now" && f.severity <= 1 && part139)
    mandatory = {
      is: true,
      why: t("a severity-5 rule with a {framework} limit fired on the platform's readings", {
        framework: clause.framework,
      }),
    };
  /* judged on the finding's severity and phase, not its headline, which is written in the reader's language:
     a test finding is severity 1 only when it failed, an obligation is "now" only when overdue */
  else if (f.id.startsWith("test:") && f.severity === 1)
    mandatory = { is: true, why: t("the last mandatory test failed against its threshold") };
  else if (f.id.startsWith("obligation:") && f.phase === "now")
    mandatory = { is: true, why: t("the obligation is overdue") };
  else if (category === "certificate" && ttt !== null && ttt <= 0)
    mandatory = { is: true, why: t("the certificate has expired") };
  else if (
    condition.value != null &&
    condition.limit != null &&
    (condition.direction === "higher_is_better" ? condition.value < condition.limit : condition.value > condition.limit)
  )
    mandatory = {
      is: true,
      why: t("the measured condition is past the {framework} limit", { framework: clause.framework }),
    };

  const evidence = [...f.prediction.evidence, ...f.attribution.evidence, ...f.cost.evidence]
    .map(fromEvidence)
    .concat(trend.sources, surfaceSources, costSources)
    .filter((e, i, all) => all.findIndex((x) => x.kind === e.kind && x.ref === e.ref) === i);

  return {
    id: `decision:${f.id}`,
    findingId: f.id,
    asset,
    zone,
    category,
    headline: f.headline,
    condition,
    trend,
    clause,
    surface: { zoneClass, closureMinutes, movementsPerHour, standType, sources: surfaceSources },
    timeToThresholdDays: ttt,
    mandatory,
    cost,
    window,
    missing,
    evidence,
  };
}

/* ── §3 score ─────────────────────────────────────────────────────────── */

const STAND_FACTOR: Record<ZoneClass, number> = {
  runway: 1,
  taxiway: 0.6,
  stand: 0.5,
  apron: 0.3,
  terminal: 0.1,
  other: 0.1,
};

export function score(item: Omit<DecisionItem, "score" | "options">, c: Constraints, windowsFree: number): Score {
  const comps: Component[] = [];
  const s = item.surface;
  const factor = s.zoneClass === "stand" && s.standType === "remote" ? 0.25 : STAND_FACTOR[s.zoneClass];
  if (s.closureMinutes != null && s.movementsPerHour != null) {
    const v = clamp(((s.closureMinutes / 60) * s.movementsPerHour * factor) / 200);
    comps.push({
      key: "consequence",
      label: t("Consequence"),
      value: v,
      working: t("{minutes} min ÷ 60 × {movements} movements/h × {factor} ({zone}) ÷ 200 = {value}", {
        minutes: s.closureMinutes,
        movements: s.movementsPerHour,
        factor,
        zone: `${s.zoneClass}${s.standType !== "n/a" ? `, ${s.standType}` : ""}`,
        value: v.toFixed(2),
      }),
      sources: s.sources,
    });
  } else
    comps.push({
      key: "consequence",
      label: t("Consequence"),
      value: null,
      working: t("not computable: closure minutes or movements per hour missing"),
      sources: s.sources,
    });

  if (item.timeToThresholdDays != null) {
    const v = 1 - clamp(item.timeToThresholdDays / 180);
    comps.push({
      key: "timeToThreshold",
      label: t("Time to threshold"),
      value: v,
      working: t("{days} days to the limit; 1 − {days}/180 = {value} — {basis}", {
        days: item.timeToThresholdDays,
        value: v.toFixed(2),
        basis: item.trend.basis,
      }),
      sources: item.trend.sources,
    });
  } else
    comps.push({
      key: "timeToThreshold",
      label: t("Time to threshold"),
      value: null,
      working: t("not computable — {basis}", { basis: item.trend.basis }),
      sources: item.trend.sources,
    });

  if (item.mandatory.is)
    comps.push({
      key: "regulatoryExposure",
      label: t("Regulatory exposure"),
      value: null,
      working: t("not scored: the limit is crossed, so this is mandatory action, not a trade-off"),
      sources: item.clause.sources,
    });
  else if (item.condition.value != null && item.condition.limit != null && item.condition.limit !== 0) {
    const distance = 1 - clamp(Math.abs(item.condition.value - item.condition.limit) / Math.abs(item.condition.limit));
    const eff = item.clause.effect === "certificate_action" ? 1 : item.clause.effect === "finding" ? 0.6 : 0.3;
    const v = clamp(distance * eff);
    comps.push({
      key: "regulatoryExposure",
      label: t("Regulatory exposure"),
      value: v,
      working: t("distance to the limit {distance} × effect {effect} ({clause}) = {value}", {
        distance: distance.toFixed(2),
        effect: eff,
        clause: `${item.clause.effect.replace("_", " ")}${item.clause.clause ? `, ${item.clause.clause}` : ""}`,
        value: v.toFixed(2),
      }),
      sources: [...item.clause.sources, ...item.condition.sources],
    });
  } else if (item.clause.clause) {
    const eff = item.clause.effect === "certificate_action" ? 1 : item.clause.effect === "finding" ? 0.6 : 0.3;
    comps.push({
      key: "regulatoryExposure",
      label: t("Regulatory exposure"),
      value: eff * 0.5,
      working: t("no measured distance to the limit; the clause's effect alone ({effect}) at half weight = {value}", {
        effect: item.clause.effect.replace("_", " "),
        value: (eff * 0.5).toFixed(2),
      }),
      sources: item.clause.sources,
    });
  } else
    comps.push({
      key: "regulatoryExposure",
      label: t("Regulatory exposure"),
      value: null,
      working: t("not computable: no clause on the rule and no limit on record"),
      sources: [],
    });

  if (item.cost.now != null && item.cost.year != null && item.cost.now > 0) {
    const v = clamp((item.cost.year - item.cost.now) / item.cost.now);
    comps.push({
      key: "deferralCost",
      label: t("Deferral cost curve"),
      value: v,
      working: t("{now} today → {year} in a year; (year − now) ÷ now = {value}", {
        now: money(item.cost.now, item.cost.currency),
        year: money(item.cost.year, item.cost.currency),
        value: v.toFixed(2),
      }),
      sources: item.cost.sources,
    });
  } else
    comps.push({
      key: "deferralCost",
      label: t("Deferral cost curve"),
      value: null,
      working: t("not priced"),
      sources: item.cost.sources,
    });

  const scarcity = c.windows > 0 ? 1 - clamp(windowsFree / c.windows) : 1;
  const crewPenalty =
    c.scarceCrew && item.window.crew && item.window.crew.toLowerCase().includes(c.scarceCrew.toLowerCase()) ? 0.2 : 0;
  const seasonPenalty = item.window.season && c.season && item.window.season !== c.season ? 0.2 : 0;
  const w = clamp((item.window.needsNight ? scarcity : 0) + crewPenalty + seasonPenalty);
  comps.push({
    key: "windowConstraint",
    label: t("Window constraint"),
    value: w,
    working: `${item.window.needsNight ? t("needs a night window; {free} of {windows} free → {scarcity}", { free: windowsFree, windows: c.windows, scarcity: scarcity.toFixed(2) }) : t("no night window needed → 0")}${crewPenalty ? t(" + scarce crew {penalty}", { penalty: crewPenalty }) : ""}${seasonPenalty ? t(" + out of season {penalty}", { penalty: seasonPenalty }) : ""} = ${w.toFixed(2)}`,
    sources: item.window.sources,
  });

  const present = comps.filter((x) => x.value != null);
  const wsum = present.reduce((t, x) => t + c.weights[x.key], 0);
  const total = wsum > 0 ? present.reduce((t, x) => t + c.weights[x.key] * (x.value as number), 0) / wsum : null;
  return {
    components: comps,
    total: total != null ? Math.round(total * 100) / 100 : null,
    coverage: t("{n} of 5 components", { n: present.length }),
    weights: c.weights,
  };
}

/* ── §5 options ───────────────────────────────────────────────────────── */

function options(item: Omit<DecisionItem, "options">, card: RateCard): [Option, Option, Option] {
  const r = item.score.components.find((x) => x.key === "regulatoryExposure")?.value ?? null;
  const cat = card.categories[item.category];
  const cur = item.cost.currency;
  const act: Option = {
    key: "act_now",
    label: t("Act now"),
    cost: item.cost.now ?? null,
    closureMinutes: item.surface.closureMinutes ?? null,
    exposure: 0,
    displaces: [],
    note: t("{cost} now{closure}; the exposure is retired.", {
      cost: item.cost.now != null ? money(item.cost.now, cur) : t("unpriced"),
      closure:
        item.surface.closureMinutes != null
          ? t(", {n} min of closure this quarter", { n: item.surface.closureMinutes })
          : "",
    }),
  };
  const mitigate: Option = {
    key: "defer_mitigate",
    label: t("Defer with mitigation"),
    cost: item.cost.quarter != null ? item.cost.quarter + cat.mitigationCost : null,
    closureMinutes: item.surface.closureMinutes ?? null,
    exposure: r != null ? Math.round(r * 50) / 100 : null,
    mitigation: cat.mitigation,
    displaces: [],
    note: t("{mitigation}; {cost}; exposure halved while the mitigation holds{limit}.", {
      mitigation: cat.mitigation,
      cost:
        item.cost.quarter != null
          ? t("{quarter} next quarter plus {mitigation} of mitigation", {
              quarter: money(item.cost.quarter, cur),
              mitigation: money(cat.mitigationCost, cur),
            })
          : t("unpriced"),
      limit: item.timeToThresholdDays != null ? t("; {n} days to the limit", { n: item.timeToThresholdDays }) : "",
    }),
  };
  const accept: Option = {
    key: "defer_accept",
    label: t("Defer and accept"),
    cost: item.cost.quarter ?? null,
    closureMinutes: item.surface.closureMinutes ?? null,
    exposure:
      r != null
        ? Math.min(
            1,
            Math.round((r + (item.timeToThresholdDays != null && item.timeToThresholdDays <= 90 ? 0.4 : 0.2)) * 100) /
              100,
          )
        : null,
    displaces: [],
    note: t("{cost}{year}; exposure rises{limit}.", {
      cost:
        item.cost.quarter != null
          ? t("{amount} next quarter", { amount: money(item.cost.quarter, cur) })
          : t("unpriced"),
      year: item.cost.year != null ? t(", {amount} in a year", { amount: money(item.cost.year, cur) }) : "",
      limit:
        item.timeToThresholdDays != null
          ? t(" and the limit is reached in {n} days, when this becomes mandatory", { n: item.timeToThresholdDays })
          : "",
    }),
  };
  return [act, mitigate, accept];
}

/* ── §6 the solver ────────────────────────────────────────────────────── */

export interface Portfolio {
  constraints: Constraints;
  mandatory: DecisionItem[];
  chosen: DecisionItem[];
  slipped: { item: DecisionItem; slipCost: number | null; why: string }[];
  used: { windows: number; budget: number; crewNights: number };
  available: { windows: number; budget: number; crewNights: number };
  infeasible?: string;
  objective: number;
}

function value(item: DecisionItem, w: Weights): number {
  const g = (k: Component["key"]) => item.score.components.find((x) => x.key === k)?.value ?? 0;
  return (
    w.consequence * g("consequence") +
    w.regulatoryExposure * g("regulatoryExposure") +
    w.deferralCost * g("deferralCost") +
    0.05 * g("timeToThreshold")
  );
}
const use = (i: DecisionItem) => ({
  cost: i.cost.now ?? 0,
  windows: i.window.needsNight ? 1 : 0,
  crewNights: i.window.crewNights,
});

export function solve(items: DecisionItem[], c: Constraints): Portfolio {
  const mandatory = items.filter((i) => i.mandatory.is);
  const discretionary = items.filter((i) => !i.mandatory.is);
  const total = { windows: c.windows, budget: c.budget, crewNights: c.crews * c.nightsPerCrew };
  const mand = mandatory.reduce(
    (t, i) => ({
      cost: t.cost + use(i).cost,
      windows: t.windows + use(i).windows,
      crewNights: t.crewNights + use(i).crewNights,
    }),
    { cost: 0, windows: 0, crewNights: 0 },
  );
  const avail = {
    windows: total.windows - mand.windows,
    budget: total.budget - mand.cost,
    crewNights: total.crewNights - mand.crewNights,
  };
  const base: Portfolio = {
    constraints: c,
    mandatory,
    chosen: [],
    slipped: [],
    used: { windows: mand.windows, budget: mand.cost, crewNights: mand.crewNights },
    available: total,
    objective: 0,
  };
  if (avail.windows < 0 || avail.budget < 0 || avail.crewNights < 0) {
    return {
      ...base,
      slipped: discretionary.map((item) => ({
        item,
        slipCost: item.cost.quarter != null && item.cost.now != null ? item.cost.quarter - item.cost.now : null,
        why: t("nothing discretionary fits: the mandatory work alone exceeds the quarter"),
      })),
      infeasible: t(
        "The mandatory work alone needs {windows} windows, {cost} and {crewNights} crew-nights against {totalWindows}, {budget} and {totalCrewNights}. That is the decision: more resources, or a mandatory item that cannot be done in the quarter.",
        {
          windows: mand.windows,
          cost: money(mand.cost, c.weights ? (items[0]?.cost.currency ?? "SAR") : "SAR"),
          crewNights: mand.crewNights,
          totalWindows: total.windows,
          budget: money(total.budget, items[0]?.cost.currency ?? "SAR"),
          totalCrewNights: total.crewNights,
        },
      ),
    };
  }
  const fits = (set: DecisionItem[]) => {
    const u = set.reduce(
      (t, i) => ({
        cost: t.cost + use(i).cost,
        windows: t.windows + use(i).windows,
        crewNights: t.crewNights + use(i).crewNights,
      }),
      { cost: 0, windows: 0, crewNights: 0 },
    );
    return u.cost <= avail.budget && u.windows <= avail.windows && u.crewNights <= avail.crewNights;
  };
  /* density: value per weighted resource, each resource weighted by its scarcity */
  const density = (i: DecisionItem) => {
    const u = use(i);
    const weight =
      (avail.budget > 0 ? u.cost / avail.budget : 0) +
      (avail.windows > 0 ? u.windows / avail.windows : u.windows) +
      (avail.crewNights > 0 ? u.crewNights / avail.crewNights : 0) +
      0.01;
    return value(i, c.weights) / weight;
  };
  const ordered = [...discretionary].sort((a, b) => density(b) - density(a));
  let chosen: DecisionItem[] = [];
  for (const i of ordered) if (fits([...chosen, i])) chosen = [...chosen, i];
  /* pairwise swap improvement */
  let improved = true;
  while (improved) {
    improved = false;
    const out = discretionary.filter((i) => !chosen.includes(i));
    for (const a of chosen) {
      for (const b of out) {
        const trial = chosen.filter((x) => x !== a).concat(b);
        if (
          fits(trial) &&
          trial.reduce((t, i) => t + value(i, c.weights), 0) >
            chosen.reduce((t, i) => t + value(i, c.weights), 0) + 1e-9
        ) {
          chosen = trial;
          improved = true;
          break;
        }
      }
      if (improved) break;
    }
  }
  const used = chosen.reduce(
    (t, i) => ({
      windows: t.windows + use(i).windows,
      budget: t.budget + use(i).cost,
      crewNights: t.crewNights + use(i).crewNights,
    }),
    { windows: mand.windows, budget: mand.cost, crewNights: mand.crewNights },
  );
  const slipped = discretionary
    .filter((i) => !chosen.includes(i))
    .map((item) => {
      const u = use(item);
      const why =
        u.windows > avail.windows - (used.windows - mand.windows)
          ? t("no night window left")
          : u.cost > avail.budget - (used.budget - mand.cost)
            ? t("over the budget left")
            : u.crewNights > avail.crewNights - (used.crewNights - mand.crewNights)
              ? t("no crew-nights left")
              : t("outranked on value per resource");
      return {
        item,
        slipCost: item.cost.quarter != null && item.cost.now != null ? item.cost.quarter - item.cost.now : null,
        why,
      };
    });
  /* displacement: what each chosen item's window pushed out */
  for (const i of chosen) {
    const displaced = slipped.filter((s) => s.item.window.needsNight && i.window.needsNight).map((s) => s.item.asset);
    i.options[0].displaces = displaced.slice(0, 3);
  }
  return {
    ...base,
    chosen,
    slipped,
    used,
    objective: Math.round(chosen.reduce((t, i) => t + value(i, c.weights), 0) * 100) / 100,
  };
}

/* ── the engine ───────────────────────────────────────────────────────── */

export interface EngineOutput {
  items: DecisionItem[];
  portfolio: Portfolio;
  case: ExecutiveCase;
  /** every number the engine emitted, for §7's check */
  figures: Set<string>;
  confidence: { shown: boolean; note: string };
  generatedAt: string;
}

/** An alert and the overdue work order it raised are one item; the alert carries the rule and the clause. */
function oneItemPerProblem(findings: Finding[]): Finding[] {
  const cited = new Set(
    findings
      .filter((f) => f.id.startsWith("alert:"))
      .flatMap((f) => f.attribution.evidence.filter((e) => e.kind === "work_order").map((e) => e.ref)),
  );
  return findings.filter((f) => !(f.id.startsWith("sla:") && f.prediction.evidence.some((e) => cited.has(e.ref))));
}

export function runEngine(inp: EngineInputs): EngineOutput {
  const normalised = oneItemPerProblem(inp.report.findings).map((f) => normalise(f, inp));
  const nightNeeded = normalised.filter((i) => i.window.needsNight).length;
  const windowsFree = Math.max(0, inp.constraints.windows - nightNeeded);
  const items: DecisionItem[] = normalised.map((n) => {
    const sc = score(n, inp.constraints, windowsFree);
    const withScore = { ...n, score: sc };
    return { ...withScore, options: options(withScore, inp.rateCard) };
  });
  items.sort(
    (a, b) => (b.mandatory.is ? 1 : 0) - (a.mandatory.is ? 1 : 0) || (b.score.total ?? -1) - (a.score.total ?? -1),
  );
  const portfolio = solve(items, inp.constraints);
  const exec = writeCase(items, portfolio);
  const figures = collectFigures(items, portfolio);
  const gate = 30;
  return {
    items,
    portfolio,
    case: exec,
    figures,
    confidence: {
      shown: inp.resolvedPredictions >= gate,
      note:
        inp.resolvedPredictions >= gate
          ? t("{n} resolved predictions on the ledger", { n: inp.resolvedPredictions })
          : t("confidence withheld — {predictions} on the ledger, {gate} needed", {
              predictions: tn(inp.resolvedPredictions, "{n} resolved prediction", "{n} resolved predictions"),
              gate,
            }),
    },
    generatedAt: inp.now.toISOString(),
  };
}

/* ── §7 the case, deterministic ───────────────────────────────────────── */

/** The asset where there is one; otherwise the first words of the headline. */
export function nameOf(i: DecisionItem): string {
  return i.asset === "site" ? i.headline.replace(/\s+—.*$/, "").slice(0, 48) : i.asset;
}

export interface ExecutiveCase {
  recommendation: string;
  reasoning: string[];
  counterArgument: string;
  wouldChange: string[];
}

export function writeCase(items: DecisionItem[], p: Portfolio): ExecutiveCase {
  const cur = items[0]?.cost.currency ?? "SAR";
  const recommendation = p.infeasible
    ? p.infeasible
    : t(
        "{mandatory} first — not a decision. Then {chosen} of {total} discretionary items this quarter: {names}. Uses {windows} of {totalWindows} windows, {budget} of {totalBudget}, {crewNights} of {totalCrewNights} crew-nights.",
        {
          mandatory: tn(p.mandatory.length, "{n} mandatory item", "{n} mandatory items"),
          chosen: p.chosen.length,
          total: p.chosen.length + p.slipped.length,
          names: p.chosen.map(nameOf).join(", ") || t("none"),
          windows: p.used.windows,
          totalWindows: p.available.windows,
          budget: money(p.used.budget, cur),
          totalBudget: money(p.available.budget, cur),
          crewNights: p.used.crewNights,
          totalCrewNights: p.available.crewNights,
        },
      );
  const reasoning = p.chosen.slice(0, 5).map((i) => {
    const top = [...i.score.components]
      .filter((c) => c.value != null)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))[0];
    return t("{name}: {headline} — chosen on {basis}; score {score} on {coverage}.", {
      name: nameOf(i),
      headline: i.headline,
      basis: top
        ? `${top.label.toLowerCase()} ${top.value?.toFixed(2)} (${top.working})`
        : t("the components available"),
      score: i.score.total ?? "—",
      coverage: i.score.coverage,
    });
  });
  const worst = [...p.slipped].sort((a, b) => (b.item.score.total ?? 0) - (a.item.score.total ?? 0))[0];
  const counterArgument = worst
    ? t(
        "The strongest case against this allocation is {name}: {headline}, slipped because {why}. Slipping it costs {cost} over the quarter{limit}. If that is unacceptable, the trade is {trade}'s window.",
        {
          name: nameOf(worst.item),
          headline: worst.item.headline,
          why: worst.why,
          cost: worst.slipCost != null ? money(worst.slipCost, cur) : t("an unpriced amount"),
          limit:
            worst.item.timeToThresholdDays != null
              ? t(" and the limit is {n} days away", { n: worst.item.timeToThresholdDays })
              : "",
          trade: (() => {
            const c = p.chosen.find((x) => x.window.needsNight);
            return c ? nameOf(c) : t("one of the chosen items");
          })(),
        },
      )
    : t("Nothing was slipped, so the case against this allocation is only that it spends what it spends.");
  const wouldChange: string[] = [];
  const missingCount = items.reduce((t, i) => t + i.missing.length, 0);
  if (missingCount)
    wouldChange.push(
      t(
        "{inputs} across {items} items are missing or from the starting rate card; entering them changes the consequence and cost terms first.",
        {
          inputs: tn(missingCount, "{n} input", "{n} inputs"),
          items: items.filter((i) => i.missing.length).length,
        },
      ),
    );
  const unpriced = items.filter((i) => i.cost.now == null).length;
  if (unpriced)
    wouldChange.push(
      t("{items} unpriced: a repair estimate on each would let the solver weigh them.", {
        items: tn(unpriced, "{n} item", "{n} items"),
      }),
    );
  const noTrend = items.filter((i) => i.timeToThresholdDays == null).length;
  if (noTrend)
    wouldChange.push(
      t("{items} with no computable time to threshold: a second reading, or an entered rate, gives them a date.", {
        items: tn(noTrend, "{n} item", "{n} items"),
      }),
    );
  wouldChange.push(
    t(
      "A change to the quarter's windows, budget or crews re-runs the solver; the weights are visible and editable, and the total moves with them.",
    ),
  );
  return { recommendation, reasoning, counterArgument, wouldChange };
}

/* ── §7 the model layer's check ───────────────────────────────────────── */

function collectFigures(items: DecisionItem[], p: Portfolio): Set<string> {
  const out = new Set<string>();
  const add = (n: number | null | undefined) => {
    if (n == null || Number.isNaN(n)) return;
    out.add(String(Math.round(n)));
    out.add(n.toFixed(2).replace(/\.?0+$/, ""));
    out.add(Math.round(n).toLocaleString("en"));
  };
  for (const i of items) {
    add(i.condition.value);
    add(i.condition.limit);
    add(i.trend.ratePerDay);
    add(i.timeToThresholdDays);
    add(i.surface.closureMinutes);
    add(i.surface.movementsPerHour);
    add(i.cost.now);
    add(i.cost.quarter);
    add(i.cost.year);
    add(i.cost.twoYears);
    add(i.window.crewNights);
    add(i.score.total);
    for (const c of i.score.components) add(c.value);
    for (const o of i.options) {
      add(o.cost);
      add(o.closureMinutes);
      add(o.exposure);
    }
  }
  add(p.used.windows);
  add(p.used.budget);
  add(p.used.crewNights);
  add(p.available.windows);
  add(p.available.budget);
  add(p.available.crewNights);
  add(p.objective);
  add(p.mandatory.length);
  add(p.chosen.length);
  add(p.slipped.length);
  add(items.length);
  for (const s of p.slipped) add(s.slipCost);
  for (let n = 0; n <= 12; n++) out.add(String(n));
  return out;
}

/** Every number in the text must be one the engine emitted. Returns the offenders. */
export function verifyFigures(text: string, figures: Set<string>): string[] {
  const found = text.match(/\d[\d,]*(?:\.\d+)?/g) ?? [];
  return [
    ...new Set(
      found.filter(
        (f) =>
          !figures.has(f) && !figures.has(f.replace(/,/g, "")) && !figures.has(String(Number(f.replace(/,/g, "")))),
      ),
    ),
  ];
}

export const MODEL_SYSTEM_PROMPT = [
  "You are explaining a maintenance decision engine's output to an airport chief executive.",
  "You may explain, compare, challenge and answer questions about the figures you are given.",
  "You must not state any number that is not in the material you are given. Do not estimate, extrapolate or round differently.",
  "Refer to items by their asset and cite the record references given. Be brief and plain.",
].join(" ");

/** The engine's output as the only material the model sees. */
export function materialForModel(out: EngineOutput): string {
  const lines: string[] = [
    `Quarter ${out.portfolio.constraints.quarter}. Windows ${out.portfolio.available.windows}, budget ${out.portfolio.available.budget}, crew-nights ${out.portfolio.available.crewNights}.`,
  ];
  for (const i of out.items) {
    lines.push(
      `Item ${i.asset} [${i.findingId}] ${i.headline}. ${i.mandatory.is ? `MANDATORY: ${i.mandatory.why}.` : `Score ${i.score.total ?? "n/a"} (${i.score.coverage}).`} Condition: ${i.condition.label}. Trend: ${i.trend.basis}. Time to threshold: ${i.timeToThresholdDays ?? "n/a"} days. Clause: ${i.clause.clause || "none"}. Closure minutes: ${i.surface.closureMinutes ?? "n/a"}. Movements/h: ${i.surface.movementsPerHour ?? "n/a"}. Cost now ${i.cost.now ?? "n/a"}, quarter ${i.cost.quarter ?? "n/a"}, year ${i.cost.year ?? "n/a"}. Crew ${i.window.crew ?? "n/a"}, ${i.window.crewNights} crew-nights, night window ${i.window.needsNight ? "yes" : "no"}.`,
    );
    for (const c of i.score.components) lines.push(`  ${c.label}: ${c.value ?? "n/a"} — ${c.working}`);
    for (const o of i.options)
      lines.push(
        `  Option ${o.label}: cost ${o.cost ?? "n/a"}, closure ${o.closureMinutes ?? "n/a"} min, exposure after ${o.exposure ?? "n/a"}${o.displaces.length ? `, displaces ${o.displaces.join(", ")}` : ""}. ${o.note}`,
      );
    if (i.missing.length) lines.push(`  Missing: ${i.missing.map((m) => m.field).join(", ")}`);
  }
  lines.push(
    `Chosen: ${out.portfolio.chosen.map((i) => i.asset).join(", ") || "none"}. Slipped: ${out.portfolio.slipped.map((s) => `${s.item.asset} (${s.why}${s.slipCost != null ? `, slip cost ${s.slipCost}` : ""})`).join("; ") || "none"}.`,
  );
  lines.push(`Case: ${out.case.recommendation} ${out.case.reasoning.join(" ")} Counter: ${out.case.counterArgument}`);
  return lines.join("\n");
}

/* ── §8 the log ───────────────────────────────────────────────────────── */

export interface DecisionRecord {
  id: string;
  at: string;
  by: string;
  itemId: string;
  asset: string;
  headline: string;
  option: Option["key"];
  mitigation?: string;
  rationale: string;
  evidence: SourceRef[];
  scoreAtDecision: { total: number | null; coverage: string; components: { key: string; value: number | null }[] };
  timeToThresholdDays: number | null;
  portfolioAt: string;
}

/** A deferral with a threshold date is a prediction with a date: what the ledger will resolve. */
export function recordFor(
  item: DecisionItem,
  option: Option["key"],
  by: string,
  rationale: string,
  portfolioAt: string,
  mitigation?: string,
): DecisionRecord {
  return {
    id: `${item.id}:${Date.now()}`,
    at: new Date().toISOString(),
    by,
    itemId: item.id,
    asset: item.asset,
    headline: item.headline,
    option,
    mitigation,
    rationale,
    evidence: item.evidence,
    scoreAtDecision: {
      total: item.score.total,
      coverage: item.score.coverage,
      components: item.score.components.map((c) => ({ key: c.key, value: c.value })),
    },
    timeToThresholdDays: item.timeToThresholdDays,
    portfolioAt,
  };
}

/* ── follow-up questions the engine can answer itself ─────────────────── */

export function answerFollowUp(
  q: string,
  out: EngineOutput,
): { text: string; resolveWith?: Partial<Constraints> } | null {
  const ql = q.toLowerCase();
  const num = (re: RegExp) => {
    const m = ql.match(re);
    return m ? Number(m[1].replace(/,/g, "")) : undefined;
  };
  const windows = num(/(\d+)\s*(?:night\s*)?windows?/);
  const budget = num(/(?:budget|sar)\s*(?:of\s*)?([\d,]+)/) ?? num(/([\d,]{5,})\s*(?:sar|budget)/);
  const crews = num(/(\d+)\s*crews?/);
  if (/what if|had|were|with|given/.test(ql) && (windows != null || budget != null || crews != null)) {
    return {
      text: t("Re-running the solver with {changes}.", {
        changes: [
          windows != null ? t("{n} windows", { n: windows }) : null,
          budget != null ? t("a budget of {n}", { n: budget }) : null,
          crews != null ? t("{n} crews", { n: crews }) : null,
        ]
          .filter(Boolean)
          .join(", "),
      }),
      resolveWith: {
        ...(windows != null ? { windows } : {}),
        ...(budget != null ? { budget } : {}),
        ...(crews != null ? { crews } : {}),
      },
    };
  }
  const byAsset = out.items.find(
    (i) => ql.includes(i.asset.toLowerCase()) || ql.includes(i.headline.toLowerCase().slice(0, 24)),
  );
  if (byAsset && /why|rank|score|above|below|before/.test(ql)) {
    const comps = byAsset.score.components
      .map((c) => `${c.label} ${c.value != null ? c.value.toFixed(2) : "—"}: ${c.working}`)
      .join("\n");
    const place = out.portfolio.mandatory.includes(byAsset)
      ? t("mandatory — not a decision")
      : out.portfolio.chosen.includes(byAsset)
        ? t("chosen this quarter")
        : t("slipped: {why}", { why: String(out.portfolio.slipped.find((s) => s.item === byAsset)?.why) });
    return {
      text: `${t("{asset} — {headline}. {place}. Score {score} on {coverage}.", {
        asset: byAsset.asset,
        headline: byAsset.headline,
        place,
        score: byAsset.score.total ?? "—",
        coverage: byAsset.score.coverage,
      })}\n${comps}`,
    };
  }
  if (/slip|displac|not done|drop/.test(ql)) {
    return {
      text: out.portfolio.slipped.length
        ? out.portfolio.slipped
            .map(
              (s) =>
                `${nameOf(s.item)}: ${s.why}${s.slipCost != null ? t(", slip cost {amount}", { amount: money(s.slipCost, s.item.cost.currency) }) : ""}`,
            )
            .join("\n")
        : t("Nothing slipped."),
    };
  }
  if (/mandatory|not a decision|must/.test(ql)) {
    return {
      text: out.portfolio.mandatory.length
        ? out.portfolio.mandatory.map((i) => `${nameOf(i)}: ${i.mandatory.why}`).join("\n")
        : t("Nothing is mandatory this quarter."),
    };
  }
  return null;
}
